/**
 * Plausible Analytics (Business plan) — thin wrapper around `window.plausible`.
 *
 * The actual script tag is injected by the consent-gated bootstrap in
 * `index.html` (mounted on production hosts when the user has granted
 * consent). This module just provides typed helpers for firing events
 * via the queue-safe `window.plausible` shim that `index.html` always
 * installs.
 *
 * Loader: per-account script `pa-FMoZ94kMMSXwmOMsQCmOj.js` (Plausible's
 * newer "Web Analytics" loader — extensions are configured in the
 * Plausible dashboard, not via URL suffixes).
 *
 * No PII is sent — Plausible's model is anonymous and we do not pass
 * user IDs as event props.
 */

import { isProductionAnalyticsHost } from "./analyticsHost";


const PLAUSIBLE_SCRIPT_SRC = "https://plausible.io/js/pa-FMoZ94kMMSXwmOMsQCmOj.js";
const SCRIPT_MARKER_ATTR = "data-excerptus-analytics";
const IGNORE_KEY = "plausible_ignore";

type PlausibleEventOptions = {
  props?: Record<string, string | number | boolean>;
  revenue?: { amount: number; currency: string };
  callback?: () => void;
};

type PlausibleFn = ((name: string, options?: PlausibleEventOptions) => void) & {
  q?: unknown[];
  o?: unknown;
  init?: (config?: unknown) => void;
};

declare global {
  interface Window {
    plausible?: PlausibleFn;
  }
}

const gatesPass = (): boolean => {
  if (typeof window === "undefined") return false;
  if (!isProductionAnalyticsHost()) return false;
  try {
    if (window.localStorage.getItem("plausible_ignore") === "true") return false;
  } catch {
    /* ignore */
  }
  return true;
};


/**
 * Ensure the per-account Plausible script tag is present in <head>.
 * Safe to call multiple times — no-op if the script is already loaded.
 * The `index.html` gate handles the initial load on page open; this
 * function exists so a runtime consent grant (cookie banner accept)
 * can inject the script without a page reload.
 */
export const ensurePlausibleLoaded = (): void => {
  if (!gatesPass()) return;
  if (typeof document === "undefined") return;
  // Initialize the queue shim if it isn't already.
  if (!window.plausible) {
    const stub: PlausibleFn = function (...args: unknown[]) {
      (stub.q = stub.q || []).push(args);
    } as PlausibleFn;
    stub.init = function (i?: unknown) {
      stub.o = i || {};
    };
    window.plausible = stub;
  }
  const existing = document.querySelector(`script[${SCRIPT_MARKER_ATTR}="plausible"]`);
  if (existing) return;
  const s = document.createElement("script");
  s.async = true;
  s.src = PLAUSIBLE_SCRIPT_SRC;
  s.setAttribute(SCRIPT_MARKER_ATTR, "plausible");
  document.head.appendChild(s);
  try {
    window.plausible?.init?.();
  } catch {
    /* swallow */
  }
};

/** Fire a custom event with optional props. No-op without consent / on non-prod hosts. */
export const trackEventPlausible = (
  name: string,
  props?: Record<string, string | number | boolean>,
): void => {
  if (!gatesPass()) return;
  try {
    window.plausible?.(name, props ? { props } : undefined);
  } catch {
    /* swallow */
  }
};

/**
 * Fire a revenue event (e.g. `Purchase`). Plausible Business shows
 * revenue charts and revenue funnels for any custom event marked as a
 * revenue goal in the dashboard.
 */
export const trackRevenue = (
  name: string,
  payload: { amount: number; currency: string } & Record<string, string | number | boolean>,
): void => {
  if (!gatesPass()) return;
  const { amount, currency, ...rest } = payload;
  try {
    window.plausible?.(name, {
      revenue: { amount, currency },
      props: Object.keys(rest).length ? (rest as Record<string, string | number | boolean>) : undefined,
    });
  } catch {
    /* swallow */
  }
};

/** Honor a consent revocation. Plausible's documented opt-out flag. */
export const shutdownPlausible = (): void => {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(IGNORE_KEY, "true");
  } catch {
    /* swallow */
  }
};

/** Re-enable on consent grant — clears Plausible's opt-out flag. */
export const enablePlausible = (): void => {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.removeItem(IGNORE_KEY);
  } catch {
    /* swallow */
  }
};
